import { markVersionReloadPending } from "./updateReloadDraft";

/**
 * Simplified Version Check Utility
 * Automatically checks for new app versions and forces reload
 */

const VERSION_CHECK_INTERVAL = 30 * 60 * 1000; // Check every 30 minutes

/**
 * Check if a new version is available by fetching index.html headers
 */
export const checkForNewVersion = async (): Promise<boolean> => {
  try {
    // Fetch index.html with cache-busting timestamp
    const response = await fetch(`/?t=${Date.now()}`, {
      method: "HEAD",
      cache: "no-cache",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) {
      console.warn("Version check failed:", response.status);
      return false;
    }

    // Check ETag or Last-Modified header
    const etag = response.headers.get("etag");
    const lastModified = response.headers.get("last-modified");
    const storedEtag = localStorage.getItem("app_etag");
    const storedLastModified = localStorage.getItem("app_last_modified");

    // First time - store current version
    if (!storedEtag && !storedLastModified) {
      if (etag) localStorage.setItem("app_etag", etag);
      if (lastModified) localStorage.setItem("app_last_modified", lastModified);
      return false;
    }

    // Check if version changed
    if (etag && etag !== storedEtag) {
      console.log("New version detected (ETag):", etag);
      return true;
    }

    if (lastModified && lastModified !== storedLastModified) {
      console.log("New version detected (Last-Modified):", lastModified);
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error checking for new version:", error);
    return false;
  }
};

/**
 * Force reload the app with cache clearing
 */
export const reloadApp = (): void => {
  console.log("Reloading app with cache clear...");

  // Mark this as a version-triggered reload so draft recovery can run once.
  markVersionReloadPending();

  // Clear service worker cache if exists
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => registration.unregister());
    });
  }

  // Clear all caches
  if ("caches" in window) {
    caches.keys().then((names) => {
      names.forEach((name) => caches.delete(name));
    });
  }

  // Clear non-auth localStorage items
  try {
    const keysToKeep: string[] = [];
    // Preserve Supabase auth tokens
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("sb-")) {
        keysToKeep.push(key);
      }
    }

    const valuesToKeep = keysToKeep.map((key) => ({
      key,
      value: localStorage.getItem(key),
    }));

    localStorage.clear();

    // Restore auth tokens
    valuesToKeep.forEach(({ key, value }) => {
      if (value) localStorage.setItem(key, value);
    });
  } catch (error) {
    console.error("Error clearing localStorage:", error);
  }

  // Force hard reload
  window.location.reload();
};

/**
 * Initialize version checking
 * Returns cleanup function to stop checking
 */
export const initVersionCheck = (): (() => void) => {
  let timeoutId: number | undefined;

  const performCheck = async () => {
    try {
      const hasNewVersion = await checkForNewVersion();

      if (hasNewVersion) {
        console.log("New version available - reloading in 3 seconds...");
        // No user interaction needed - just reload after brief delay
        setTimeout(() => {
          reloadApp();
        }, 3000);
        return; // Don't schedule next check
      }

      // Schedule next check
      timeoutId = window.setTimeout(performCheck, VERSION_CHECK_INTERVAL);
    } catch (error) {
      console.error("Version check error:", error);
      // Schedule next check even on error
      timeoutId = window.setTimeout(performCheck, VERSION_CHECK_INTERVAL);
    }
  };

  // Start first check after initial delay (1 minute after app load)
  timeoutId = window.setTimeout(performCheck, 60 * 1000);

  // Cleanup function
  return () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  };
};
